// Kamojo product design: running total on the add-to-cart buttons, image arrows
// over the gallery, and the sticky purchase bar. Cart requests stay with Dawn's product-form.js.
(() => {
  // Shopify rounds a percentage discount down per item, so totals are priced per item the same way.
  const discounted = (price, quantity, percent) => (price - Math.floor((price * percent) / 100)) * quantity;

  const formatMoney = (cents, format) => {
    const value = Number(cents) || 0;
    const group = (decimals, thousands = ',', decimal = '.') => {
      const [whole, fraction] = (value / 100).toFixed(decimals).split('.');
      return whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (fraction ? decimal + fraction : '');
    };
    return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (match, key) => {
      if (key === 'amount_no_decimals') return group(0);
      if (key === 'amount_with_comma_separator') return group(2, '.', ',');
      if (key === 'amount_no_decimals_with_comma_separator') return group(0, '.');
      if (key === 'amount_with_apostrophe_separator') return group(2, "'");
      return group(2);
    });
  };

  if (!customElements.get('kj-product-controls')) {
    customElements.define(
      'kj-product-controls',
      class extends HTMLElement {
        connectedCallback() {
          this.info = this.closest('product-info');
          if (!this.info) return;
          this.sectionId = this.info.dataset.section;
          this.format = this.dataset.moneyFormat;
          // Compare-at prices and savings stay plain; only main prices carry the currency code.
          this.plainFormat = this.dataset.moneyPlainFormat || this.format;
          try {
            this.variant = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
          } catch (error) {
            this.variant = null;
          }

          const addButton = this.info.querySelector(`#ProductSubmitButton-${this.sectionId}`);
          if (addButton && !addButton.querySelector('[data-kj-total]')) {
            const total = document.createElement('span');
            total.className = 'kj-product__total';
            total.dataset.kjTotal = '';
            const spinner = addButton.querySelector('.loading__spinner');
            spinner ? spinner.before(total) : addButton.append(total);
          }

          this.onChange = (event) => {
            if (event.type === 'change' && event.target.matches('[data-kj-tier]')) {
              this.setQuantity(Number(event.target.value));
              return;
            }
            if (event.target.matches('[data-kj-addon], .quantity__input')) this.update();
          };
          this.onClick = (event) => {
            const arrow = event.target.closest('[data-kj-media-step]');
            if (arrow) this.step(Number(arrow.dataset.kjMediaStep));
          };
          this.info.addEventListener('change', this.onChange);
          this.info.addEventListener('input', this.onChange);
          this.info.addEventListener('click', this.onClick);

          if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
              if (!data || data.sectionId !== this.sectionId) return;
              this.variant = data.variant || null;
              this.update();
            });
          }
          this.update();
        }

        disconnectedCallback() {
          this.info?.removeEventListener('change', this.onChange);
          this.info?.removeEventListener('input', this.onChange);
          this.info?.removeEventListener('click', this.onClick);
          this.unsubscribe?.();
        }

        quantity() {
          const input = this.info.querySelector('.quantity__input');
          return Math.max(1, parseInt(input?.value, 10) || 1);
        }

        // Quantity discount tiers: picking one sets the quantity through Dawn's quantity input.
        setQuantity(quantity) {
          const input = this.info.querySelector('.quantity__input');
          if (!input || !quantity) return;
          input.value = quantity;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // The highest tier the current quantity reaches (mirrors an automatic "minimum quantity" discount).
        tierFor(quantity) {
          return [...this.info.querySelectorAll('[data-kj-tier]')]
            .filter((tier) => Number(tier.value) <= quantity)
            .sort((a, b) => Number(b.value) - Number(a.value))[0];
        }

        updateTiers(variant, quantity) {
          const tiers = [...this.info.querySelectorAll('[data-kj-tier]')];
          if (!tiers.length) return 0;
          const active = this.tierFor(quantity);
          tiers.forEach((tier) => {
            tier.checked = tier === active;
            if (!variant) return;
            const label = tier.closest('label');
            const full = variant.price * Number(tier.value);
            const now = discounted(variant.price, Number(tier.value), Number(tier.dataset.percent));
            const nowEl = label.querySelector('[data-kj-tier-now]');
            const wasEl = label.querySelector('[data-kj-tier-was]');
            if (nowEl) nowEl.textContent = formatMoney(now, this.format);
            if (wasEl) wasEl.textContent = formatMoney(full, this.plainFormat);
          });
          return active ? Number(active.dataset.percent) || 0 : 0;
        }

        update() {
          const variant = this.variant;
          const quantity = this.quantity();
          const percent = this.updateTiers(variant, quantity);
          const addons = [...this.info.querySelectorAll('[data-kj-addon]:checked')].reduce(
            (sum, addon) => sum + (Number(addon.dataset.price) || 0),
            0
          );
          const products = variant ? discounted(variant.price, quantity, percent) : 0;
          const total = variant ? products + addons : 0;
          this.info.querySelectorAll('[data-kj-total]').forEach((element) => {
            element.textContent = variant ? `· ${formatMoney(total, this.format)}` : '';
          });

          const stickyPrice = this.info.querySelector('[data-kj-sticky-price]');
          if (!stickyPrice || !variant) return;
          const price = document.createElement('strong');
          price.textContent = formatMoney(variant.price, this.format);
          const parts = [price];
          if (variant.compare_at_price > variant.price) {
            const compare = document.createElement('s');
            compare.textContent = formatMoney(variant.compare_at_price, this.plainFormat);
            const save = document.createElement('span');
            save.className = 'kj-product-sticky__save';
            save.textContent = (this.dataset.saveLabel || '[amount]').replace(
              '[amount]',
              formatMoney(variant.compare_at_price - variant.price, this.plainFormat)
            );
            parts.push(' ', compare, ' ', save);
          }
          stickyPrice.replaceChildren(...parts);
        }

        // Steps through the visible thumbnails so Dawn's media-gallery keeps the active image, zoom and video state.
        step(direction) {
          const gallery = this.info.querySelector('media-gallery');
          if (!gallery) return;
          const thumbs = [...gallery.querySelectorAll('.thumbnail-list__item')].filter((item) => item.offsetParent !== null);
          if (thumbs.length > 1) {
            const current = Math.max(0, thumbs.findIndex((item) => item.querySelector('[aria-current]')));
            const target = thumbs[(current + direction + thumbs.length) % thumbs.length];
            target.querySelector('button')?.click();
            // Desktop thumbnails sit in a vertical column; keep the active one in view.
            const list = target.parentElement;
            if (list && list.scrollHeight > list.clientHeight + 1) {
              list.scrollTo({
                top: target.offsetTop - (list.clientHeight - target.offsetHeight) / 2,
                behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
              });
            }
            return;
          }
          gallery.querySelector(`.slider-button--${direction > 0 ? 'next' : 'prev'}`)?.click();
        }
      }
    );
  }

  if (!customElements.get('kj-product-sticky')) {
    customElements.define(
      'kj-product-sticky',
      class extends HTMLElement {
        connectedCallback() {
          this.info = this.closest('product-info');
          this.source = this.info?.querySelector('product-form [name="add"]');
          this.button = this.querySelector('[data-kj-sticky-submit]');
          this.label = this.querySelector('[data-kj-sticky-label]');
          if (!this.source || !this.button) return;

          this.sync = () => {
            this.button.disabled = this.source.disabled || this.source.getAttribute('aria-disabled') === 'true';
            const text = this.source.querySelector('span')?.textContent.trim();
            if (text && this.label) this.label.textContent = text;
          };
          this.onClick = () => {
            this.sync();
            if (!this.button.disabled) this.source.form?.requestSubmit(this.source);
          };
          this.button.addEventListener('click', this.onClick);

          // Shown once the main button has scrolled above the viewport. A scroll check (not an
          // IntersectionObserver) so jumping straight back to the top hides it again.
          this.onScroll = () => {
            if (this.frame) return;
            this.frame = requestAnimationFrame(() => {
              this.frame = null;
              const button = this.source.getBoundingClientRect();
              this.hidden = button.height === 0 || button.bottom > 0;
            });
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });
          window.addEventListener('resize', this.onScroll, { passive: true });
          this.onScroll();
          this.mutation = new MutationObserver(this.sync);
          this.mutation.observe(this.source, {
            subtree: true,
            childList: true,
            characterData: true,
            attributes: true,
            attributeFilter: ['disabled', 'aria-disabled'],
          });
          this.sync();
        }

        disconnectedCallback() {
          window.removeEventListener('scroll', this.onScroll);
          window.removeEventListener('resize', this.onScroll);
          if (this.frame) cancelAnimationFrame(this.frame);
          this.mutation?.disconnect();
          this.button?.removeEventListener('click', this.onClick);
        }
      }
    );
  }
})();
